import React, { memo, useRef, useState, useEffect } from 'react';
import { useStore } from 'store/StoreContext';
import { Link, useHistory } from 'react-router-dom';
import { RoutePath } from 'consts/RoutePath';
import { HttpStatus } from 'consts/HttpStatus';
import { ApiError } from 'requests/RequestBase';
import { ReactError } from 'consts/ReactError';
import {
	callWithdrawalReasonApi,
	ServiceAgreement,
	GetServiceAgreementResData,
	callGetServiceAgreementApi,
	WithdrawalReason,
	GetWithdrawalReasonResData,
	callGetQuestionApi,
	callSendMailApi,
	callGetIsLockApi,
	GetIsLockResData,
	initialIsLockData,
} from 'requests/RequestUser';
import { callSingleLogoutApi } from 'requests/RequestLogin';
import { copyObject } from 'common/copyObject';
import { clearGenerator } from 'store/login-user/LoginUserData';
import classes from './override.module.scss';
import { CircularProgress, Backdrop } from '@material-ui/core';
import { Hr } from 'components/atoms/Hr';

type Props = {};

interface CheckStatus {
	[key: string]: boolean;
}
type WithdrawForm = {
	checkStatus: CheckStatus;
	reasonDetail: string;
};

export const WithdrawTemplate = memo((_props: Props) => {
	const [store, dispatch] = useStore();
	const history = useHistory<{ mailaddress: string }>();
	const mailaddress = store.loginUserData.mailaddress_1;
	const [page, setPage] = useState(0);

	const [lockedServices, setLockedServices] = useState<ServiceAgreement[]>([]);
	const [withdrawalReasons, setWithdrawalReasons] = useState<WithdrawalReason[]>([]);
	const [isLockResData, setIsLockResData] = useState<GetIsLockResData>(initialIsLockData);
	const [isGetDataLoading, setIsGetDataLoading] = useState(false);
	useEffect(() => {
		const execute = async () => {
			setIsGetDataLoading(true);

			const results = await Promise.all([
				callGetServiceAgreementApi({ gidpklist: store.loginUserData.gidpk }),
				callGetQuestionApi(),
				callGetIsLockApi(),
			]);
			const errorResult = results.find((r) => r.status !== HttpStatus.OK);
			if (errorResult) {
				const errorData = errorResult.data as ApiError;
				console.log(errorData.message);
				history.push(RoutePath.ERROR);
				setIsGetDataLoading(false);
				return;
			}

			const [serviceAgreementResult, questionResult, isLockResult] = results;
			const serviceAgreementData = serviceAgreementResult.data as GetServiceAgreementResData;
			setLockedServices(serviceAgreementData.gid_list[0].service_agreement_list.filter((d) => d.withdrawal_lock === 1));
			const questionData = questionResult.data as GetWithdrawalReasonResData;
			setWithdrawalReasons(questionData.withdrawal_reason_list);
			const isLockData = isLockResult.data as GetIsLockResData;
			setIsLockResData(isLockData);

			setIsGetDataLoading(false);
		};
		execute();
	}, []);

	const [withdrawForm, setWithdrawForm] = useState<WithdrawForm>({
		checkStatus: {},
		reasonDetail: '',
	});
	const [formErrors, setFormErrors] = useState<{ reasonDetail: string }>({ reasonDetail: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	useEffect(() => {
		if (!withdrawalReasons.length) {
			return;
		}
		// checkStatusはcheckboxで扱いやすいようにオブジェクトに変換
		setWithdrawForm({
			checkStatus: withdrawalReasons.reduce((result, r) => {
				result[r.withdrawal_reason_cd] = false;
				return result;
			}, {} as CheckStatus),
			reasonDetail: '',
		});
	}, [withdrawalReasons]);

	const handleChangeCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		const formData = copyObject(withdrawForm.checkStatus);
		formData[event.target.name] = event.target.checked;
		setWithdrawForm((prev) => ({ ...prev, checkStatus: formData }));
	};
	const handleChangeValue = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setWithdrawForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};

	const [isWithdrawLoading, setIsWithdrawLoading] = useState(false);
	const withdraw = async () => {
		setIsWithdrawLoading(true);
		// validation
		setFormErrors((prev) => ({ ...prev, reasonDetail: '' }));
		const REASON_DETAIL_MAX = 4000;
		if (withdrawForm.reasonDetail.length > REASON_DETAIL_MAX) {
			setFormErrors((prev) => ({ ...prev, reasonDetail: ReactError.E0011 }));
			setIsWithdrawLoading(false);
			return;
		}
		// チェックされているcodeの抽出
		const checkedCodeList = Object.entries(withdrawForm.checkStatus)
			.filter((s) => s[1])
			.map((s) => ({ withdrawal_reason_cd: s[0] }));
		// その他(999)を追加
		if (!checkedCodeList.some((cc) => cc.withdrawal_reason_cd === '999')) {
			checkedCodeList.push({ withdrawal_reason_cd: '999' });
		}
		const withdrawResult = await callWithdrawalReasonApi({
			withdrawal_reason_cd_list: checkedCodeList,
			withdrawal_reason_detail: withdrawForm.reasonDetail,
		});
		if (withdrawResult.status !== HttpStatus.OK) {
			const errorData = withdrawResult.data as ApiError;
			console.log(errorData.message);
			history.push(RoutePath.ERROR);
			setIsWithdrawLoading(false);
			return;
		}

		// 退会完了メール送信
		if (!!mailaddress) {
			await callSendMailApi({ send_type: 2 });
		}

		const logoutResult = await callSingleLogoutApi();
		if (logoutResult.status !== HttpStatus.OK) {
			history.push(RoutePath.ERROR);
			setIsWithdrawLoading(false);
			return;
		}
		clearGenerator(dispatch);

		setIsWithdrawLoading(false);
		setPage((prev) => prev + 1);
	};

	const isServiceLock = !!lockedServices?.length;
	const serviceLockNote = () => {
		return (
			<main>
				<div className={classes.greenBack}>
					<p className={classes.greenBackTitle}>現在ご利用中のサービス</p>
					<ul>
						{lockedServices.map((s) => {
							return (
								<li>
									<a href={s.setting_change_url} target="_blank">
										{s.display_site_name}
									</a>
								</li>
							);
						})}
					</ul>
				</div>
				<div className="section-txt-box">
					<div>ご利用いただいている上記サービスの登録解除・解約が必要となります。</div>
					<div>登録解除・解約については各サービスの事務局へお問い合わせください。</div>
					<div>また、上記以外の各サービスの登録情報がある場合も、サービスサイト内で別途お手続きください。</div>
				</div>
			</main>
		);
	};

	const isFamilyLock = isLockResData.is_lock || (isLockResData.is_representative && isLockResData.member_exist);
	// この関数が呼ばれる段階では必ずisFamilyLockを満たす
	const familyLockNote = () => {
		if (isLockResData.is_representative) {
			return (
				<>
					<div className={classes.greenBack}>
						<p className={classes.greenBackTitle}>{`代表者のため退会することができません`}</p>
					</div>
					<div className="section-txt-box">
						<div>「家族情報」へ遷移後「代表者を変更する」より代表者変更を行ってください。</div>
						<div>
							「家族」のメンバーがご自身と「GakkenこどもID」のみの場合は、連携しているこどもIDを削除する必要があります。
						</div>
					</div>
				</>
			);
		}

		// 代表者以外の場合
		return (
			<>
				<div className={classes.greenBack}>
					<p className={classes.greenBackTitle}>{`「家族」に所属しているため退会することができません`}</p>
				</div>
				<div className="section-txt-box">
					<div>「家族情報へ遷移後「家族から抜ける」よりお手続きをお願いいたします。</div>
					<div>※必ず「自分だけ抜ける」を選択してください</div>
				</div>
			</>
		);
	};

	const returnTop = () => {
		clearTimer();
		window.location.href = '/';
	};
	const [timer, setTimer] = useState(undefined);
	const clearTimer = () => {
		clearTimeout(timer);
	};
	useEffect(() => {
		if (page === 2) {
			const timer = setTimeout(function () {
				window.location.href = '/';
			}, 5000);
			setTimer(timer);
		}
	}, [page]);

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID 退会';
	}, []);

	useEffect(() => {
		if (isFormError) {
			scrollRef.current.scrollIntoView();
		}
	}, [formErrors]);

	return (
		<>
			<Backdrop className={classes.backdrop} open={isGetDataLoading || isWithdrawLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<section className="section mypage">
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>{`GakkenIDを退会する`}</li>
					</ul>
				</div>
				<h1 className="page-head" ref={scrollRef}>{`GakkenIDを退会する`}</h1>
				<div className="section-in">
					{page === 0 && (
						<>
							<div className={`section-head ${classes.note}`}>
								<span>注意事項をご確認ください</span>
							</div>
							<div className="section-txt-box">
								<p>
									GakkenIDを退会すると、GakkenIDを使用するサービスが利用できなくなります。また、GakkenIDに登録されているすべてのデータも参照できなくなります。
								</p>
								<div>
									退会手続き後は、マイページのご利用ができなくなり、退会したGakkenIDについては再開することはできません。
								</div>
								<div className="btn-box">
									<div className="btn wh">
										<button
											onClick={() => {
												setPage((prev) => prev + 1);
											}}
										>
											退会する
										</button>
									</div>
									<div className="btn gr">
										<Link to={RoutePath.MY_PAGE}>戻る</Link>
									</div>
								</div>
							</div>
						</>
					)}
					{page === 1 && (
						<>
							<div className={`section-head ${classes.note}`}>
								<span>注意事項をご確認ください</span>
							</div>
							{isServiceLock || isFamilyLock ? (
								<>
									{isServiceLock && serviceLockNote()}
									{isServiceLock && isFamilyLock && <Hr />}
									{isFamilyLock && familyLockNote()}
									<div className="btn-box">
										<div className="btn gr">
											<button
												onClick={() => {
													setPage((prev) => prev - 1);
												}}
											>
												戻る
											</button>
										</div>
									</div>
								</>
							) : (
								<>
									{isFormError && (
										<div className="error-box">
											<p>※下記項目は全て必須となります。</p>
											<div className="error-box-in">
												{formErrors.reasonDetail !== '' ? <p>【退会理由詳細】{formErrors.reasonDetail}</p> : null}
											</div>
										</div>
									)}

									<div className="section-txt-box">
										GakkenIDへのご登録、誠にありがとうございました。恐れ入りますが今後より良いサービスを提供していくために、アンケートにご協力いただけますと幸いです。
									</div>
									<div className="form-box">
										<div className="form-head">
											<label htmlFor="">退会理由をお選びください</label>
											<span>（※複数選択可）</span>
										</div>
										<div className="form-input">
											<div className="check-box">
												<ul>
													{withdrawalReasons.map((r) => {
														return (
															<li>
																<label
																	className={`check-label ${
																		withdrawForm.checkStatus[r.withdrawal_reason_cd] ? 'is-active' : ''
																	}`}
																	htmlFor={r.withdrawal_reason_cd}
																>
																	<input
																		type="checkbox"
																		id={r.withdrawal_reason_cd}
																		name={r.withdrawal_reason_cd}
																		checked={withdrawForm.checkStatus[r.withdrawal_reason_cd]}
																		onChange={handleChangeCheckValue}
																	/>
																	{r.withdrawal_reason_label}
																</label>
															</li>
														);
													})}
												</ul>
											</div>
										</div>
									</div>
									<div className="form-box">
										<div className="form-head">
											<label htmlFor="">ご意見・ご要望をぜひお聞かせください</label>
										</div>
										<div className="form-input">
											<textarea
												className={classes.textarea}
												name="reasonDetail"
												placeholder="ご記入ください。"
												rows={10}
												value={withdrawForm.reasonDetail}
												onChange={handleChangeValue}
											/>
										</div>
										<p className="error">{formErrors.reasonDetail}</p>
									</div>

									<div className="btn-box">
										<div className="btn wh">
											<button onClick={withdraw}>退会する</button>
										</div>
										<div className="btn gr">
											<button
												onClick={() => {
													setPage((prev) => prev - 1);
												}}
											>
												戻る
											</button>
										</div>
									</div>
								</>
							)}
						</>
					)}
					{page === 2 && (
						<>
							<div className="thankyou-box">
								<img src="images/withdraw.svg" alt="ご利用ありがとうございました" />
							</div>
							<p>5秒後に自動的にTOPページへ戻ります</p>
							<div className="btn-box">
								<div className="btn gr">
									<button onClick={returnTop}>TOPページへ戻る</button>
								</div>
							</div>
						</>
					)}
				</div>
			</section>
		</>
	);
});
